import { FC, memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Flex } from '@rebass/emotion';
import { css } from '@emotion/core';

import useCart from '@app/shop/hooks/useCart';
import { isNotNullish } from '@app/helpers/predicate';

import { Total, GrandTotal, TaxInfo } from './CartTotals.styles';
import messages from './CartTotals.messages';

const CartTotals: FC = () => {
  const { prices, storeConfig } = useCart();
  const {
    // Represented by "Subtotal"
    subtotalExcludingTax,
    subtotalIncludingTax,
    // Listed individually
    discounts,
    // Represented by "Order Total Exc. Tax"
    subtotalWithDiscountExcludingTax,
    // Listed individually
    appliedTaxes,
    // Represented by Order Total
    grandTotal,
  } = prices ?? {};
  const validTaxes = useMemo(() => appliedTaxes?.filter(isNotNullish), [
    appliedTaxes,
  ]);
  const validDiscounts = useMemo(() => discounts?.filter(isNotNullish), [
    discounts,
  ]);

  const includingTax = storeConfig?.taxSalesDisplaySubtotal === 'including';

  return (
    <dl>
      {includingTax
        ? subtotalIncludingTax && (
            <Total
              data-testid="cart-totals-subtotal"
              key="subtotal"
              label={<FormattedMessage {...messages.subtotal} />}
              amount={subtotalIncludingTax}
            />
          )
        : subtotalExcludingTax && (
            <Total
              data-testid="cart-totals-subtotal"
              key="subtotal"
              label={<FormattedMessage {...messages.subtotal} />}
              amount={subtotalExcludingTax}
            />
          )}

      {validDiscounts?.map(({ label, amount }) => (
        <Total
          data-testid={`cart-totals-discount-${label}`}
          key={`discount-${label}`}
          label={
            <>
              <FormattedMessage {...messages.discountLabel} />
            </>
          }
          amount={{ ...amount, value: (amount?.value ?? 0) * -1 }}
        />
      )) ?? null}

      {validDiscounts?.length && subtotalWithDiscountExcludingTax && (
        <Total
          data-testid="cart-totals-total-ex-tax"
          key="totalExTax"
          label={<FormattedMessage {...messages.totalExTax} />}
          amount={subtotalWithDiscountExcludingTax}
        />
      )}

      <Flex
        css={css`
          margin-bottom: 1rem;
        `}
      >
        {validTaxes?.map(({ label, amount }) => (
          <>
            <Total
              data-testid={`cart-totals-tax-${label}`}
              key={label}
              label={
                <>
                  {includingTax ? (
                    <FormattedMessage {...messages.inclTax} />
                  ) : (
                    <FormattedMessage {...messages.exTax} />
                  )}
                  {label && ` (${label})`}
                </>
              }
              amount={amount}
            />
            {'*'}
          </>
        )) ?? null}
      </Flex>

      {grandTotal && (
        <GrandTotal
          data-testid="cart-totals-grand-total"
          key="grandTotal"
          label={<FormattedMessage {...messages.grandTotal} />}
          amount={grandTotal}
        />
      )}

      {!!validTaxes?.length && (
        <TaxInfo>
          {'*'}
          <FormattedMessage {...messages.taxCheckout} />
        </TaxInfo>
      )}
    </dl>
  );
};

export default memo(CartTotals);
